import React, { Component } from "react";
import T from "prop-types";
import { stringify } from "query-string";
import { renderIf } from "MODULES/shared/decorators";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import { matchesString, matchesBoolean } from "LIB/filters";
import { API_BASE, CLEAR_FILTERS_TEXT } from "APP_ROOT/constants";
import {
  FilterSelect,
  Pagination,
  SearchableFilterSelect,
  SearchFilter
} from "MODULES/shared/components/Pagination";
import { Button, Search } from "MODULES/shared/components";
import {
  SALES,
  ACCOUNTING,
  ADMIN,
  REPAIR,
  ROLE,
  USER,
  FIRST_NAME,
  BUYER_ID,
  EXPORT_LIMIT,
  WAREHOUSE_LOGISTICS_DIRECTOR,
  CAN_VIEW_ALL_INVENTORY,
  QUALITY_CONTROL
} from "MODULES/users/constants";
import api from "APP_ROOT/api";
import { ATL, BTL, BBTL, ZTL, SATL } from "MODULES/parts/constants";
import {
  DO_NOT_LIST,
  DO_NOT_SELL,
  GONE,
  LIST,
  OUT_ON_EXCHANGE,
  QC_HOLD,
  SCRAP
} from "MODULES/inventories/constants";
import { SUCCESS } from "LIB/submission";
import _, { debounce, flatten, get, uniq } from "lodash";
import { CONSIGNMENT, EMA } from "MODULES/Receiving/constants";
import { WithCurrentUser } from "MODULES/session/decorators/withCurrentUser";
import { hasAnyOfRoles, hasAnyOfRolesOrSubroles } from "MODULES/users/helpers";
import InventoryStats from "./InventoryStats";
import Table from "./Table";
import Title from "./Title";
import pagination from "../../pagination";

const defaultFilters = {
  as_removed: undefined,
  beyond_econ_repair: undefined,
  inspected: undefined,
  sellable_red_status: undefined,
  sellable_green_status: undefined,
  sellable_yellow_status: undefined,
  no_srp: undefined,
  not_gone: undefined,
  has_cost: undefined,
  qc_status: undefined,
  new_item: undefined,
  open_discrepancies: undefined,
  location_at_shop: undefined,
  out_on_exchange: undefined,
  out_of_stock: undefined,
  overhauled: undefined,
  repaired: undefined,
  scrap: undefined,
  date_received_under_6_mos: undefined,
  date_received_6_to_12_mos: undefined,
  date_received_12_to_18_mos: undefined,
  date_received_over_18_mos: undefined,
  repair_and_hold: undefined,
  repair_and_hold_lead_time: undefined,
  status: undefined,
  condition: undefined,
  has_scrap_report: undefined,
  parts_unit: undefined,
  tier_type: undefined,
  transfer: undefined,
  consignment: undefined,
  lot: undefined,
  tag_date: undefined,
  store_owner_id: undefined,
  do_not_sell: undefined,
  do_not_list: undefined,
  do_not_exchange: undefined,
  my_inventory: undefined
};

const baseFilters = [
  {
    label: "Color Bar = Green",
    filters: { sellable_green_status: LIST }
  },
  {
    label: "Color Bar = Yellow",
    filters: { sellable_yellow_status: DO_NOT_LIST }
  },
  {
    label: "Color Bar = Red",
    filters: { sellable_red_status: DO_NOT_SELL }
  },
  {
    label: "Overhauled",
    filters: { overhauled: true }
  },
  {
    label: "Repaired",
    filters: { repaired: true }
  },
  {
    label: "Inspected",
    filters: { inspected: true }
  },
  {
    label: "New",
    filters: { new_item: true }
  },
  {
    label: "As Removed",
    filters: { as_removed: true }
  },
  {
    label: "Beyond Repair",
    filters: { beyond_econ_repair: true }
  },
  {
    label: "Parts Unit",
    filters: { parts_unit: true }
  },
  {
    label: "Scrap",
    filters: { scrap: true }
  },
  {
    label: "No SRP",
    filters: { no_srp: true }
  },
  {
    label: "Only Remaining Inventory",
    filters: { not_gone: true }
  },
  {
    label: "Gone from Stock Inventory",
    filters: { out_of_stock: true }
  },
  {
    label: "Inventory with Remaining Cost",
    filters: { has_cost: true }
  },
  {
    label: "Inventory with QC Hold",
    filters: { qc_status: QC_HOLD }
  },
  {
    label: "Inventory with Open Discrepancy",
    filters: { open_discrepancies: true }
  },
  {
    label: "Location @Shop",
    filters: { location_at_shop: true }
  },
  {
    label: "Out On Exchange",
    filters: { status: OUT_ON_EXCHANGE }
  },
  {
    label: "Date Received - < 6 mos",
    filters: { date_received_under_6_mos: true }
  },
  {
    label: "Date Received - 6 to 12 mos",
    filters: { date_received_6_to_12_mos: true }
  },
  {
    label: "Date Received - 12 to 18 mos",
    filters: { date_received_12_to_18_mos: true }
  },
  {
    label: "Date Received - > 18 mos",
    filters: { date_received_over_18_mos: true }
  },
  {
    label: "Do Not List",
    filters: { do_not_list: true }
  },
  {
    label: "Do Not Sell",
    filters: { do_not_sell: true }
  },
  {
    label: "Do Not Exchange",
    filters: { do_not_exchange: true }
  },
  {
    label: "Reserved For Sale",
    filters: { status: "reserve_for_sale,on_order_for_sale" }
  },
  {
    label: "Reserved For Repair",
    filters: { repair_status: "reserve_for_repair" }
  },
  {
    label: "Sold",
    filters: { sold: true }
  },
  {
    label: "Tag Date > 5 Years",
    filters: { tag_date: "over_5_years" }
  },
  {
    label: "All Scrapped Inventory",
    filters: { condition: SCRAP, status: GONE, has_scrap_report: true }
  },
  {
    label: "Only 5 Star",
    filters: { tier_type: SATL },
    withAuthorization: { roles: [SALES, ADMIN] }
  },
  {
    label: "Only 4 Star",
    filters: { tier_type: ATL },
    withAuthorization: { roles: [SALES, ADMIN] }
  },
  {
    label: "Only 3 Star",
    filters: { tier_type: BTL },
    withAuthorization: { roles: [SALES, ADMIN] }
  },
  {
    label: "Only 2 Star",
    filters: { tier_type: BBTL },
    withAuthorization: { roles: [SALES, ADMIN] }
  },
  {
    label: "Only 1 Star",
    filters: { tier_type: ZTL },
    withAuthorization: { roles: [SALES, ADMIN] }
  },
  {
    label: "Non-Qualifiers",
    filters: { tier_type: "" },
    withAuthorization: { roles: [SALES, ADMIN] }
  },
  {
    label: "Active Transfer",
    filters: { transfer: true }
  },
  {
    label: "Repair & Hold",
    filters: { repair_and_hold: true }
  },
  {
    label: "Repair & Hold - LT",
    filters: { repair_and_hold_lead_time: true }
  },
  {
    label: "Consignment Inventory",
    filters: { consignment: CONSIGNMENT, lot: undefined }
  },
  {
    label: "Lot Inventory",
    filters: { lot: true, consignment: undefined }
  },
  {
    label: "EMA Inventory",
    filters: { consignment: EMA, lot: undefined }
  },
  {
    label: "Exclude Cons/EMAs",
    filters: { consignment: undefined, exclude_consignment: true }
  }
];

const myInventoryFilter = {
  label: "My Inventory",
  filters: { my_inventory: true }
};

const ExportButton = ({ searchProps }) => {
  const params = searchProps.paginator.get("filters").toJS();
  const inventoryTotalCount = searchProps.paginator.get("totalCount");

  return (
    <Button
      onClick={() => {
        if (inventoryTotalCount > EXPORT_LIMIT) {
          toastr.info("Processing Report.");
          api.inventories.export(params).then(() => {
            SUCCESS;
          });
        } else {
          window.open(
            `${API_BASE}/inventories/export?${stringify(params)}`,
            "_blank"
          );
          toastr.success(
            "Processing Inventory Report. Please wait for the PDF to generate."
          );
        }
      }}
    >
      Export
    </Button>
  );
};

ExportButton.propTypes = {
  searchProps: T.shape({
    paginator: T.instanceOf(Map)
  })
};

const meetsExportCriteria = renderIf(
  ({ searchProps, currentUser }) =>
    searchProps.paginator.get("totalCount") <= EXPORT_LIMIT ||
    currentUser.get(ROLE) === ADMIN
);

const DecoratedExportButton = meetsExportCriteria(ExportButton);

export class InventoriesSearch extends Component {
  state = {
    allSearchedApplications: [],
    applications: [],
    applicationSearch: "",
    applicationSearchOpen: false,
    buyersFilter: [],
    wareHouseFilter: [],
    buyersTags: [],
    ...WithCurrentUser
  };

  componentDidMount() {
    api.users
      .search({
        all_buyers: true,
        page: 1,
        sort: FIRST_NAME,
        reverse: false,
        limit: 1000
      })
      .then(resp => {
        const buyers = resp.data.results;
        const buyersOptions = buyers.map(b => ({
          label: b.full_name,
          filters: { buyer_id: b.id }
        }));
        const buyersTags = buyers
          .map(b => ({
            label: b.full_name,
            key: BUYER_ID,
            matchesTarget: matchesBoolean(b.id)
          }))
          .concat([
            {
              label: "Everyone Combined",
              key: BUYER_ID,
              matchesTarget: matchesString("all")
            }
          ]);

        this.setState({
          buyersFilter: [
            {
              label: "View All",
              filters: { buyer_id: undefined }
            }
          ].concat(buyersOptions),
          wareHouseFilter: [
            {
              label: "View All",
              filters: { ware_house_id: undefined }
            }
          ].concat(
            get(
              this.props.currentUser.toJS(),
              "tenant.ware_houses",
              []
            ).map(w => ({ label: w.name, filters: { ware_house_id: w.id } }))
          ),
          buyersTags
        });
      });
  }

  searchApplications = debounce(search => {
    if (search && search.length > 1) {
      Promise.all([
        api.engines
          .search({ limit: 100, filter: search })
          .then(({ data: { results } }) => results.map(e => e.model_number)),
        api.aircrafts
          .search({ limit: 100, filter: search })
          .then(({ data: { results } }) => results.map(e => e.model_number))
      ]).then(models => {
        this.setState({
          allSearchedApplications: uniq(
            this.state.allSearchedApplications.concat(flatten(models))
          ),
          applications: flatten(models)
        });
      });
    }
  }, 500);

  render() {
    const { currentUser } = this.props;
    const {
      applications,
      applicationSearch,
      applicationSearchOpen
    } = this.state;
    const displayBuyersFilter = ![SALES].includes(currentUser.get(ROLE));
    const displayWarehouseFilter =
      get(currentUser.toJS(), "tenant.ware_houses", []).length > 0;

    const userPermissions = currentUser.get("permissions", []);

    const shouldShowMyInventory =
      hasAnyOfRoles(currentUser.toJS(), [ACCOUNTING, QUALITY_CONTROL]) ||
      userPermissions.includes(CAN_VIEW_ALL_INVENTORY);

    const finalFilters = [...baseFilters];
    if (shouldShowMyInventory) {
      finalFilters.unshift(myInventoryFilter);
    }

    const filterWithLocations = currentUser.toJS().locations.map(l => ({
      label: `Store Owner ${l.name}`,
      filters: { store_owner_id: l.id }
    }));

    const applicationFilters = applications.map(k => ({
      label: k,
      filters: { [`application_${k}`.replace(/-|,|\(|\)| |]/g, "_")]: true }
    }));

    const applicationTags = this.state.allSearchedApplications.map(k => ({
      label: k,
      key: `application_${k}`.replace(/-|,|\(|\)| |]/g, "_")
    }));

    const tags = finalFilters.concat(applicationTags);

    return (
      <Search search={pagination} syncWithUrl>
        {searchProps => (
          <div>
            <div className="form-group form-group-searchbar has-feedback has-feedback-left">
              <i className="text-muted fa fa-search form-control-feedback form-control-feedback-left" />
              <div className="input-group">
                <SearchFilter
                  {...searchProps}
                  placeholder="Search by typing a part #, inv loc, serial #, desc., consignor, cons #, traced to, tagged by, lpo #, A/C ref # or tail #..."
                />
                <DecoratedExportButton
                  searchProps={searchProps}
                  currentUser={currentUser}
                />
                <SearchableFilterSelect
                  {...searchProps}
                  filters={applicationFilters}
                  right
                  searchValue={applicationSearch}
                  title="Appl."
                  visible={applicationSearchOpen}
                  toggle={() => {
                    this.setState({
                      applicationSearchOpen: !applicationSearchOpen
                    });
                  }}
                  searchFilters={e => {
                    const val = get(e, "target.value", "");
                    this.searchApplications(val);
                    this.setState({
                      applicationSearch: val
                    });
                  }}
                />
                {displayBuyersFilter && (
                  <FilterSelect
                    {...searchProps}
                    title="Buyer"
                    filters={this.state.buyersFilter}
                    right
                  />
                )}
                {displayWarehouseFilter && (
                  <FilterSelect
                    {...searchProps}
                    filters={this.state.wareHouseFilter || []}
                    title="Warehouse"
                    right
                  />
                )}
                <FilterSelect
                  filters={tags.concat(filterWithLocations).concat({
                    label: CLEAR_FILTERS_TEXT,
                    filters: { ...defaultFilters }
                  })}
                  {...searchProps}
                  right
                />
              </div>
            </div>
            {hasAnyOfRolesOrSubroles(
              currentUser.toJS(),
              [ADMIN, SALES, ACCOUNTING, REPAIR],
              [WAREHOUSE_LOGISTICS_DIRECTOR]
            ) && (
              <InventoryStats
                alwaysDisplayBuyerStats={!displayBuyersFilter}
                filters={searchProps.paginator.toJS().filters}
              />
            )}
            <Title
              applicationTags={applicationTags}
              searchProps={searchProps}
              buyersTags={this.state.buyersTags}
            />
            <Table {...searchProps} />
            <Pagination {...searchProps} />
          </div>
        )}
      </Search>
    );
  }
}

export default connect(state => ({ currentUser: state.session.get(USER) }))(
  InventoriesSearch
);



// WEBPACK FOOTER //
// ./app/modules/inventories/components/Search/Search.jsx